import { useMutation, useQueryClient } from '@tanstack/react-query';
import { planPaymentRequestApi, fileApi } from '@src/apis/admin';
import type { CreatePaymentRequestModel, PaymentChannel, PlanType as ApiPlanType, CommonFileDto, FileType } from '@uniquegood/realworld-admin-interface';
import type { PaymentRequestFormData } from '@src/pages/Studio/Channel/payment-request/payment-request-form-modal';
import { normalizePhoneNumberForServer } from '@src/utils/phone';

/**
 * 결제 요청 생성 mutation
 *
 * @description
 * - 채널에 결제 요청을 생성합니다.
 * - PlanPaymentRequestApi.createPaymentRequest를 사용하여 실제 API를 호출합니다.
 * - 성공 시 관련 쿼리들을 무효화하여 데이터를 갱신합니다.
 */
export const useCreatePaymentRequest = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationKey: ['createPaymentRequest'],
        mutationFn: async ({
            channelId,
            formData,
        }: {
            channelId: string;
            formData: PaymentRequestFormData;
        }) => {
            // PlanType을 API 형식으로 변환
            const planTypeMap: Record<string, ApiPlanType> = {
                'free': 'Free' as ApiPlanType,
                'pro': 'Pro' as ApiPlanType,
                'enterprise': 'Enterprise' as ApiPlanType,
                'teacher': 'Teacher' as ApiPlanType,
                'class': 'Class' as ApiPlanType,
                'realworld': 'Realworld' as ApiPlanType,
            };

            const apiPlanType = planTypeMap[formData.planType];
            if (!apiPlanType) {
                throw new Error(`Invalid plan type: ${formData.planType}`);
            }

            // PaymentChannel 변환
            const paymentChannel: PaymentChannel = formData.paymentMethod === 'TOSS_PG' 
                ? 'TossPG' as PaymentChannel
                : 'Invoice' as PaymentChannel;

            // 금액에서 쉼표 제거
            const price = parseInt(formData.amount.replace(/,/g, ''), 10);
            if (isNaN(price)) {
                throw new Error('Invalid amount');
            }

            // 세금계산서 파일 업로드 (DIRECT 결제인 경우)
            let invoiceFile: CommonFileDto | undefined;
            if (formData.paymentMethod === 'DIRECT' && formData.receiptFile) {
                // 파일 업로드 (dir: studio-payments-file, 메타데이터: 파일 이름)
                const uploadResponse = await fileApi.uploadFile(
                    formData.receiptFile.name, // Metadata: 파일 이름
                    'studio-payments-file', // dir
                    formData.receiptFile // Source
                );
                const uploadedFile = uploadResponse.data.data;
                
                if (!uploadedFile || !uploadedFile.url || !uploadedFile.id) {
                    throw new Error('File upload failed');
                }

                // 파일 타입 결정 (MIME 타입 기반)
                let fileType: FileType = 'Image';
                const mimeType = formData.receiptFile.type;
                if (mimeType === 'application/pdf') {
                    fileType = 'Other'; // PDF는 Other로 처리
                } else if (mimeType.startsWith('image/')) {
                    fileType = 'Image';
                }

                invoiceFile = {
                    fileUrl: uploadedFile.url,
                    userFileId: uploadedFile.id,
                    fileType,
                };
            }

            const requestBody: CreatePaymentRequestModel = {
                planType: apiPlanType,
                useMonths: formData.durationMonths ? parseInt(formData.durationMonths, 10) : undefined,
                price,
                paymentChannel,
                payerName: formData.managerName || null,
                payerPhone: normalizePhoneNumberForServer(formData.managerPhone),
                payerEmail: formData.managerEmail || null,
                memo: formData.note || null,
                invoiceFile,
            };

            const { data } = await planPaymentRequestApi.createPaymentRequest(
                channelId,
                undefined, // x-rwd-api-version은 default value 사용
                requestBody
            );

            return { data: data.data, channelId };
        },
        onSuccess: (response) => {
            // 관련 쿼리 무효화하여 재조회 유도
            queryClient.invalidateQueries({ queryKey: ['current-payment-request', response.channelId] });
            queryClient.invalidateQueries({ queryKey: ['plan-history', response.channelId] });
            queryClient.invalidateQueries({ queryKey: ['channel-detail', response.channelId] });
            queryClient.invalidateQueries({ queryKey: ['channels'] });
        },
    });
};

